// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars, durationVars, easeVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { INTERACTIVE_SELECTORS } from "../hooks/useClickableContainer.js";
import { Toast } from "./Toast.js";
import { ToastContext } from "./ToastContext.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  viewport: {
    kVAEAm: "xixxii4",
    kY2c9j: "x6cuj84",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kmVPX3: "x1shk3sm",
    kfzvcC: "x47corl",
    kpwlN0: "xh3736h",
    kogj98: "x1ghz6dp",
    kWkggS: "xjbqb8w",
    kVQacm: "x1rea2x4",
    $$css: true
  },
  bottomEnd: {
    krVfgx: "x1ey2m1c",
    kt4wiu: "xtijo5x",
    kGNEyG: "xuk3077",
    $$css: true
  },
  bottomStart: {
    krVfgx: "x1ey2m1c",
    kLqNvP: "x1o0tod",
    kGNEyG: "x1cy8zhl",
    $$css: true
  },
  topEnd: {
    k87sOh: "x13vifvy",
    kt4wiu: "xtijo5x",
    kGNEyG: "xuk3077",
    kXwgrk: "x3ieub6",
    $$css: true
  },
  topStart: {
    k87sOh: "x13vifvy",
    kLqNvP: "x1o0tod",
    kGNEyG: "x1cy8zhl",
    kXwgrk: "x3ieub6",
    $$css: true
  }
};
/**
 * Container that renders and manages toast notifications. Place at the root
 * of your app to enable useToast(). Toasts stack with enter/exit
 * animations and auto-promote to the CSS top layer.
 *
 * @example
 * ```
 * <ToastViewport position="bottomEnd" maxVisible={3}>
 *   <App />
 * </ToastViewport>
 * ```
 */
export function ToastViewport({
  position = 'bottomEnd',
  maxVisible = 5,
  inset,
  isTopLayer = true,
  children
}) {
  const t = useTranslator();
  const [toasts, setToasts] = useState([]);
  const [exitingIds, setExitingIds] = useState(new Set());
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  // Show the popover on mount so it enters the top layer.
  const viewportRef = useRef(null);
  // Toast ids whose exit has begun — guards onHide from double-firing (see
  // removeToast). Mirrors exitingIds state, readable synchronously.
  const exitingIdsRef = useRef(new Set());
  // The element that was focused before the user jumped into the viewport
  // (via F6). Used to restore focus once all toasts are dismissed so focus
  // never falls to <body>.
  const prevFocusRef = useRef(null);
  // When a toast is dismissed while focus lives inside it, we need to move
  // focus to a sensible neighbor after that toast unmounts. This holds the
  // id of the toast whose removal should trigger a focus handoff.
  const focusHandoffIdRef = useRef(null);
  // The next toast id that should receive focus once the dismissed toast has
  // unmounted, or 'restore' to fall back to the previously-focused element.
  const pendingFocusRef = useRef(null);

  // Collect a focusable control within a toast node, if any.
  // Reuses the canonical INTERACTIVE_SELECTORS list (native controls plus
  // role-based interactive elements) instead of a hand-rolled subset, then
  // narrows to the first candidate that can actually receive focus —
  // excluding elements opted out with `tabindex="-1"` and disabled controls.
  const getFocusable = useCallback(container => {
    if (!container) {
      return null;
    }
    const candidates = container.querySelectorAll(INTERACTIVE_SELECTORS);
    for (const candidate of candidates) {
      if (candidate.getAttribute('tabindex') === '-1' || candidate.hasAttribute('disabled') || candidate.getAttribute('aria-disabled') === 'true') {
        continue;
      }
      return candidate;
    }
    return null;
  }, []);
  const addToast = useCallback(entry => {
    setToasts(prev => {
      const {
        uniqueID,
        collisionBehavior = 'overwrite'
      } = entry.options;
      if (uniqueID) {
        const existing = prev.find(t => t.options.uniqueID === uniqueID);
        if (existing) {
          if (collisionBehavior === 'ignore') {
            return prev;
          }
          return prev.map(t => t.options.uniqueID === uniqueID ? entry : t);
        }
      }
      return [...prev, entry];
    });
  }, []);
  const removeToast = useCallback((id, reason) => {
    // An exiting toast stays in toastsRef until its exit transition ends, so
    // a second dismissal inside that window (double-click, auto-timer plus
    // manual dismiss()) would re-fire onHide. Track exiting ids in a ref —
    // the exitingIds state dedupe below runs too late to protect onHide.
    if (exitingIdsRef.current.has(id)) {
      return;
    }
    exitingIdsRef.current.add(id);
    const entry = toastsRef.current.find(t => t.id === id);
    if (entry) {
      entry.options.onHide?.(reason);
    }
    // If focus currently lives inside the toast being dismissed, remember
    // that its removal must hand focus off to a neighbor (or the element
    // focused before the user entered the viewport) rather than <body>.
    const el = viewportRef.current;
    const active = document.activeElement;
    const dismissedNode = el?.querySelector(`[data-toast-id="${id}"]`) ?? null;
    if (dismissedNode && active instanceof Node && dismissedNode.contains(active)) {
      focusHandoffIdRef.current = id;
      // Pick the neighbor to receive focus while the DOM is still intact:
      // prefer the next toast, then the previous, else restore.
      const remaining = toastsRef.current.filter(t => t.id !== id);
      if (remaining.length > 0) {
        const dismissedIndex = toastsRef.current.findIndex(t => t.id === id);
        const next = toastsRef.current[dismissedIndex + 1] ?? toastsRef.current[dismissedIndex - 1];
        pendingFocusRef.current = next ? next.id : 'restore';
      } else {
        pendingFocusRef.current = 'restore';
      }
    }
    setExitingIds(prev => {
      if (prev.has(id)) {
        return prev;
      }
      return new Set(prev).add(id);
    });
  }, []);
  const handleExited = useCallback(id => {
    exitingIdsRef.current.delete(id);
    setExitingIds(prev => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // After a dismissed toast unmounts, hand focus off so it never falls to
  // <body>. Runs in a layout effect once the toast list no longer contains
  // the dismissed toast.
  useLayoutEffect(() => {
    const handoffId = focusHandoffIdRef.current;
    const target = pendingFocusRef.current;
    if (handoffId == null || target == null) {
      return;
    }
    // Wait until the dismissed toast is actually gone from the list.
    if (toasts.some(t => t.id === handoffId)) {
      return;
    }
    focusHandoffIdRef.current = null;
    pendingFocusRef.current = null;
    const el = viewportRef.current;
    if (target !== 'restore' && el) {
      const nextNode = el.querySelector(`[data-toast-id="${target}"]`);
      const focusable = getFocusable(nextNode) ?? nextNode;
      if (focusable) {
        focusable.focus();
        return;
      }
    }
    // No remaining toast to receive focus — restore the previously-focused
    // element if it's still connected, else fall back to the container.
    const prev = prevFocusRef.current;
    prevFocusRef.current = null;
    if (prev && prev.isConnected) {
      prev.focus();
    } else if (el) {
      el.focus();
    }
  }, [toasts, getFocusable]);
  const findByUniqueID = useCallback(uid => {
    return toastsRef.current.find(t => t.options.uniqueID === uid);
  }, []);
  const contextValue = useMemo(() => ({
    addToast,
    removeToast,
    findByUniqueID
  }), [addToast, removeToast, findByUniqueID]);
  const visibleToasts = toasts.slice(-maxVisible);
  const insetStyle = {};
  if (inset?.top) {
    insetStyle.top = inset.top;
  }
  if (inset?.bottom) {
    insetStyle.bottom = inset.bottom;
  }
  if (inset?.start) {
    insetStyle.insetInlineStart = inset.start;
  }
  if (inset?.end) {
    insetStyle.insetInlineEnd = inset.end;
  }

  // Show the popover on mount so it enters the top layer
  useEffect(() => {
    if (!isTopLayer) {
      return;
    }
    const el = viewportRef.current;
    if (el && typeof el.showPopover === 'function') {
      try {
        el.showPopover();
      } catch {
        /* already showing */
      }
    }
  }, [isTopLayer]);

  // F6 jumps focus into the toast viewport — the standard "go to
  // notifications" affordance. Focus the first control in the newest toast,
  // or the viewport container if none. Toasts are non-modal, so this only
  // moves focus in; Shift+Tab / Escape let focus leave naturally.
  const hasToasts = toasts.length > 0;
  useEffect(() => {
    if (!hasToasts) {
      return;
    }
    const handleKeyDown = e => {
      if (e.key !== 'F6') {
        return;
      }
      const el = viewportRef.current;
      if (!el) {
        return;
      }
      // Already inside the viewport — nothing to do.
      const active = document.activeElement;
      if (active instanceof Node && el.contains(active)) {
        return;
      }
      e.preventDefault();
      // Remember where focus was so it can be restored on dismiss.
      if (active instanceof HTMLElement) {
        prevFocusRef.current = active;
      }
      // Newest toast is the last one rendered in the DOM.
      const toastNodes = el.querySelectorAll('[data-toast-id]');
      const newest = toastNodes[toastNodes.length - 1] ?? null;
      const focusable = getFocusable(newest) ?? newest ?? el;
      focusable.focus();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [hasToasts, getFocusable]);
  const posStyle = position === 'topEnd' ? styles.topEnd : position === 'topStart' ? styles.topStart : position === 'bottomStart' ? styles.bottomStart : styles.bottomEnd;
  return /*#__PURE__*/_jsxs(ToastContext, {
    value: contextValue,
    children: [children, /*#__PURE__*/_jsx("div", {
      ref: viewportRef,
      role: "region",
      "aria-label": t('@astryx.toast.viewport'),
      tabIndex: -1
      // popover="manual" promotes to the top layer (above dialogs).
      // Omitted inside dialogs where the viewport is already in a top layer.
      ,
      popover: isTopLayer ? 'manual' : undefined,
      ...mergeProps(stylex.props(styles.viewport, posStyle), {
        style: Object.keys(insetStyle).length > 0 ? insetStyle : undefined
      }),
      children: visibleToasts.map(entry => {
        const o = entry.options;
        const type = o.type ?? 'info';
        const isAutoHide = o.isAutoHide ?? (type === 'error' ? false : true);
        const dur = o.autoHideDuration ?? 5000;
        const isExiting = exitingIds.has(entry.id);
        return /*#__PURE__*/_jsx("div", {
          "data-toast-id": entry.id,
          ...{
            0: {
              className: "x67bb7w xrvj5dj x1tu4anv xvmdzux xjshnde xuedmi6 xsagj69 xlr8y92 x1sopeo2 x5rzir7"
            },
            1: {
              className: "x67bb7w xrvj5dj xjshnde xuedmi6 xsagj69 xlr8y92 x1sopeo2 x5rzir7 xihq33y x18d9i69"
            }
          }[!!isExiting << 0],
          onTransitionEnd: isExiting ? e => {
            if (e.propertyName === 'grid-template-rows') {
              handleExited(entry.id);
            }
          } : undefined,
          children: /*#__PURE__*/_jsx("div", {
            ...{
              className: "xb3r6kr"
            },
            children: /*#__PURE__*/_jsx(Toast, {
              type: type,
              body: o.body,
              endContent: o.endContent,
              isAutoHide: isAutoHide,
              autoHideDuration: dur,
              isExiting: isExiting,
              onDismiss: reason => removeToast(entry.id, reason)
            })
          })
        }, entry.id);
      })
    })]
  });
}
ToastViewport.displayName = 'ToastViewport';